import {
  STATE_ADDITION,
  STATE_MULTIPLIER,
} from '@wwe-portal/web/utils/superstar-details/constants';

/**
 * @private
 * Get Wrestler Ability Total Score
 */
const _calcAbilityTalentScore = (wrestler, abilities) => {
  let abilityTalentScore = 0;
  const wrestletAbilities = wrestler.Abilities;

  abilities.forEach(function (i) {
    const abilityData = wrestletAbilities.find((el) => el.ID === i.ability_id);
    if (abilityData) {
      abilityTalentScore += abilityData.levels[i.level - 1].talentScore;
    }
  });
  return abilityTalentScore;
};

/**
 * Get Wrestler Talent base on tier, fill star, level and equiped abilities
 * @param {string} tier Wrestler Tier: Bronze|Silver|Gold
 * @param {string} startedRarity Fill Star Rarity: 1_1
 * @param {number} wrestlerLevel Wrestler Level: 1
 * @param {Array} abilities The abilities is equiped to this wrestler: [{ability_id: "", level: 1}, ]
 * @param {Array} wrestlerData
 */
const calculateWrestlerTalent = (
  tier,
  startedRarity,
  wrestlerLevel,
  abilities = [],
  wrestlerData
) => {
  const wrestler = wrestlerData[tier][startedRarity];
  const talentScore = wrestler.Stats[wrestlerLevel - 1].stats.TalentScore;
  const abilityScore = _calcAbilityTalentScore(wrestler, abilities);
  return parseInt(
    (talentScore + abilityScore + STATE_ADDITION) / STATE_MULTIPLIER
  );
};

/**
 * Get Wrestler Total HP
 * @param {string} tier Wrestler Tier: Bronze|Silver|Gold
 * @param {string} startedRarity Fill Star Rarity: 1_1
 * @param {number} wrestlerLevel Wrestler Level: 1
 * @param {Array} wrestlerData
 */
const calculateWrestlerHP = (
  tier,
  startedRarity,
  wrestlerLevel,
  wrestlerData
) => {
  const wrestler = wrestlerData[tier][startedRarity];
  return wrestler.Stats[wrestlerLevel - 1].stats.HP;
};

/**
 * tier = "Bronze",
 * startedRarity = "1_1",
 * wrestlerLevel = 1,
 * trainers = [{ss_group_id: "<SSGroupId>", level: 1},]
 * @param {string} tier
 * @param {string} startedRarity
 * @param {number} wrestlerLevel
 * @param {Array} wrestlerData
 */

const calculateGemDamage = (
  tier,
  startedRarity,
  wrestlerLevel,
  wrestlerData
) => {
  const wrestler = wrestlerData[tier][startedRarity];
  const wrestlerStat = wrestler.Stats[wrestlerLevel - 1].stats;

  return {
    Black: wrestlerStat.Black,
    Red: wrestlerStat.Red,
    Blue: wrestlerStat.Blue,
    Green: wrestlerStat.Green,
    Yellow: wrestlerStat.Yellow,
    Purple: wrestlerStat.Purple,
  };
};

/**
 * tier = "Bronze",
 * startedRarity = "1_1",
 * wrestlerLevel = 1,
 * trainers = [{ss_group_id: "<SSGroupId>", level: 1},]
 * @param {Array} abilities
 * @param {string} tier
 * @param {string} startedRarity
 * @param {Array} wrestlerData
 */

const calculateAbilitiesDamage = (
  abilities,
  tier,
  startedRarity,
  wrestlerData
) => {
  const wrestler = wrestlerData[tier][startedRarity];
  const result = [];

  for (let index = 0; index < abilities.length; index++) {
    const element = abilities[index];
    const abilityId = element['ability_id'];
    const abilityLevel = element['level'];
    const ability = wrestler.Abilities.find((j) => j.ID === abilityId);
    if (ability && ability.levels) {
      const dam = ability.levels[abilityLevel - 1].dam;
      result.push(Object.assign({ dam: dam }, element));
    }
  }

  return result;
};

/**
 * tier = "Bronze",
 * startedRarity = "1_1",
 * wrestlerLevel = 1,
 * trainers = [{ss_group_id: "<SSGroupId>", level: 1},]
 * @param {string} abilityId
 * @param {number} abilityLevel
 * @param {string} tier
 * @param {string} startedRarity
 // * @param {Array} trainers
 * @param {Array} wrestlerData
 */
export const calculateAbilityDamage = (
  abilityId,
  abilityLevel,
  tier,
  startedRarity,
  wrestlerData
  // trainers = [],
) => {
  const wrestler = wrestlerData[tier][startedRarity];
  const ability =
    wrestler && wrestler.Abilities
      ? wrestler.Abilities.find((j) => j.ID === abilityId)
      : {};
  return ability && ability.levels && ability.levels[abilityLevel - 1]
    ? ability.levels[abilityLevel - 1].text
    : '';
};

/**
 * @public
 * Calculate Wrestler Data
 * tier = "Bronze|Silver|Gold",
 * starRarity = '1_1',
 * wrestlerLevel = 1,
 * wrestlerData = {} data fetched
 * @param {string} tier
 * @param {string} startedRarity
 * @param {number} wrestlerLevel
 * @param {any} wrestlerData
 * @param {Array} abilities
 * @param {Array} trainers
 */
export const calculateWrestlerData = (
  tier,
  startedRarity,
  wrestlerLevel,
  wrestlerData,
  abilities = []
) => {
  return wrestlerData && wrestlerData[tier] && wrestlerData[tier][startedRarity]
    ? {
        TalentScore: calculateWrestlerTalent(
          tier,
          startedRarity,
          wrestlerLevel,
          abilities,
          wrestlerData
        ),
        HP: calculateWrestlerHP(
          tier,
          startedRarity,
          wrestlerLevel,
          wrestlerData
        ),
        Gem: calculateGemDamage(
          tier,
          startedRarity,
          wrestlerLevel,
          wrestlerData
        ),
        Abilities: calculateAbilitiesDamage(
          abilities,
          tier,
          startedRarity,
          wrestlerData
        ),
      }
    : null;
};

/**
 * Calculate RangeSlider max value
 * @param rarity {string}
 * @param tier {string}
 */
export const calculateRangeSliderMaxValueByRarity = (rarity, tier) => {
  const level = rarity.split('_')[0];

  switch (level) {
    case '1':
      switch (tier) {
        case 'Bronze':
          return 10;
        case 'Silver':
          return 20;
        case 'Gold':
          return 30;
        default:
          return;
      }
    case '2':
      switch (tier) {
        case 'Bronze':
          return 40;
        case 'Silver':
          return 50;
        case 'Gold':
          return 60;
        default:
          return;
      }
    case '3':
      switch (tier) {
        case 'Bronze':
          return 80;
        case 'Silver':
          return 100;
        case 'Gold':
          return 120;
        default:
          return;
      }
    case '4':
      switch (tier) {
        case 'Bronze':
          return 140;
        case 'Silver':
          return 160;
        case 'Gold':
          return 180;
        default:
          return;
      }
    case '5':
      switch (tier) {
        case 'Bronze':
          return 210;
        case 'Silver':
          return 240;
        case 'Gold':
          return 250;
        default:
          return;
      }
    default:
      return;
  }
};

/**
 * Render Wrestler Gem image
 * @param key {string}
 */
export const renderWrestlerGemImage = (key) => {
  switch (key) {
    case 'Yellow':
      return '/assets/icons/Yellow.webp';
    case 'Blue':
      return '/assets/icons/Blue.webp';
    case 'Red':
      return '/assets/icons/Red.webp';
    case 'Green':
      return '/assets/icons/Green.webp';
    case 'Purple':
      return '/assets/icons/Purple.webp';
    case 'Black':
      return '/assets/icons/Black.webp';
    default:
      return;
  }
};

/**
 * Get YouTube embed URL from a YouTube link
 * @param link {string} YouTube video link
 * @returns {string} Embeddable YouTube URL
 */
export const getYouTubeEmbedUrl = (link) => {
  if (!link) return '';

  if (link.indexOf('youtu.be') >= 0) {
    return `https://www.youtube.com/embed/${link.slice(
      link.lastIndexOf('/') + 1,
      link.length
    )}`;
  }

  return `https://www.youtube.com/embed/${link.slice(
    link.lastIndexOf('v=') + 2,
    link.length
  )}`;
};
